import { useApolloClient, useMutation } from '@apollo/client'
import { toast } from 'sonner'

import { DRIVER_FRAGMENT, SHIPMENT_FRAGMENT } from '../../../../../../data/fragments'
import { ASSIGN_DRIVER, UNASSIGN_DRIVER } from '../../../../../../data/mutations/member'
import { UPDATE_SHIPMENT_STATUS } from '../../../../../../data/mutations/shipment'
import { ShipmentStatusEnum } from '../../../../../../types/shipment'
import { removeArrayItemFromCache, updateCacheFieldWithNewData } from '../../../../../../utils/graphql-cache'
import type { IAssignDriverInput } from '../../types'
/**
 * This custom hook is used to handle the mutations for adding and removing drivers from a shipment.
 * It also updates the cache with the new data after adding or removing a driver.
 */
export const useDriverMutations = (
  onClose: () => void,
  shipmentID: string,
  shipmentNumber: string,
  shipmentStatus: ShipmentStatusEnum,
  workspaceID: string | null,
  onError?: () => void
) => {
  const client = useApolloClient()

  const customHeaderContext = {
    headers: workspaceID ? { 'ws-id': workspaceID } : {}
  }

  const [assignDriver, { loading: addingDriver }] = useMutation(ASSIGN_DRIVER, {
    context: customHeaderContext,
    onCompleted: () => {
      toast.success('Driver added successfully')
    },
    onError: (err) => {
      if (err.message.includes('The conditional request failed')) {
        toast.error('Driver already exists')
        onError?.()
      } else {
        toast.error(`Failed to add driver: ${err.message}`)
        onError?.()
      }
    },
    update: updateCacheFieldWithNewData({
      cacheFieldKey: 'drivers',
      entityID: shipmentID,
      entityTypeName: 'Shipment',
      fragment: DRIVER_FRAGMENT,
      mutationResponseKey: 'assignDriver'
    })
  })

  // Mutation hook for updating the shipment status
  const [updateShipmentStatus] = useMutation(UPDATE_SHIPMENT_STATUS)

  const [unassignDriver, { loading: removingDriver }] = useMutation(UNASSIGN_DRIVER, {
    context: customHeaderContext,
    onCompleted: () => {
      toast.success('Driver removed successfully')
      const updatedShipment = client.readFragment({
        fragment: SHIPMENT_FRAGMENT,
        id: `Shipment:${shipmentID}`
      })

      if (updatedShipment) {
        const { dispatchers, drivers } = updatedShipment
        if (
          dispatchers?.length === 0 &&
          drivers?.length === 0 &&
          shipmentStatus === ShipmentStatusEnum.CarrierAssigned
        ) {
          updateShipmentStatus({
            variables: {
              id: shipmentID,
              shipmentStatus: ShipmentStatusEnum.NeedsCarrier
            }
          })
        }
      }
    },
    update: removeArrayItemFromCache({
      fieldName: 'drivers',
      id: shipmentID,
      mutationReturnValueFieldName: 'unassignDriver',
      typename: 'Shipment'
    })
  })

  const handleAddDriver = async (input: IAssignDriverInput) => {
    await assignDriver({
      variables: { ...input, shipmentID, shipmentNumber }
    })
    onClose()
  }

  const handleRemoveDriver = async ({
    userID,
    driverWorkspaceID
  }: {
    driverWorkspaceID: string | null
    userID: string
  }) => {
    await unassignDriver({
      variables: {
        driverID: userID,
        driverWorkspaceID: driverWorkspaceID,
        removedByWorkspaceID: workspaceID,
        shipmentID: shipmentID
      }
    })
  }

  return {
    handleAddDriver,
    handleRemoveDriver,
    isLoading: addingDriver || removingDriver
  }
}
